import { ApplicationsTabEnumEnum } from 'modules/applications/enums/ApplicationsTabEnum.enum';
import { JobOrderApplyStepEnum } from 'modules/jobOrderApply/types/JobOrderApplyStep.enum';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';

interface ExternalOfferSuccessRedirectParams {
  externalOfferId: string;
  jobOrderId: string;
  jobOrderAvailable: boolean;
  appliedBefore: boolean;
}

const buildUrl = (
  parts: (string | undefined)[],
  queryParams?: Record<string, string | undefined>,
) => {
  const path = parts.filter(Boolean).join('/');

  if (!queryParams) {
    return path;
  }

  const validParams: Record<string, string> = Object.fromEntries(
    Object.entries(queryParams)
      .filter(([_, value]) => typeof value === 'string')
      .map(([key, value]) => [key, value as string]),
  );

  if (Object.keys(validParams).length === 0) {
    return path;
  }

  const queryString = new URLSearchParams(validParams).toString();
  return `${path}?${queryString}`;
};

const authSitemap = Object.freeze({
  main: '/',
  onboarding: (offerId?: string) => buildUrl(['/onboarding'], { offerId }),
  jobOrder: (jobOrderId: string, externalOfferId?: string) =>
    buildUrl(['/job-orders', jobOrderId, externalOfferId || '']),
  application: (jobOrderId: string) =>
    buildUrl(['/job-orders', jobOrderId], { application: 'true' }),
  offers: '/',
  observed: '/observed',
  applications: (tabName?: ApplicationsTabEnumEnum) =>
    buildUrl(['/applications', tabName?.toLowerCase()]),
  profile: (tabName = '') => buildUrl(['/profile', tabName]),
  settings: '/settings',
  notifications: '/notifications',
  externalOffer: (externalOfferId: string) => `/offer/${externalOfferId}`,
  externalOfferSuccess: (params: ExternalOfferSuccessRedirectParams) =>
    `/offer/${params.externalOfferId}/success?joId=${params.jobOrderId}&available=${params.jobOrderAvailable}&appliedBefore=${params.appliedBefore}`,
  jobOrderApply: (
    jobOrderId: string,
    step?: JobOrderApplyStepEnum,
    externalOfferId?: string,
    fromAirInvitation?: boolean,
  ) =>
    buildUrl(['/apply', jobOrderId, step?.toLowerCase()], {
      externalOfferId,
      fromAirInvitation: fromAirInvitation ? 'true' : undefined,
    }),
});

const defaultSitemap = Object.freeze({
  main: '/',
  onboarding: (_jobOrderId?: string) => '/',
  jobOrder: (jobOrderId: string, externalOfferId?: string) =>
    buildUrl(['/job-orders', jobOrderId, externalOfferId || '']),
  application: (_jobOrderId: string) => '/',
  offers: '/',
  observed: '/',
  applications: (_tabName?: string) => '/',
  profile: (_tabName?: string) => '/',
  settings: '/',
  notifications: '/',
  externalOffer: (externalOfferId: string) => `/offer/${externalOfferId}`,
  externalOfferSuccess: (params: ExternalOfferSuccessRedirectParams) =>
    `/offer/${params.externalOfferId}/success?joId=${params.jobOrderId}&available=${params.jobOrderAvailable}&appliedBefore=${params.appliedBefore}`,
  jobOrderApply: (
    _jobOrderId: string,
    _step?: JobOrderApplyStepEnum,
    _externalOfferId?: string,
    _fromAirInvitation?: boolean,
  ) => '/',
});

export const useSitemap = () => {
  const authenticated = useAuthStore((state) => state.authenticated);
  if (authenticated) {
    return authSitemap;
  }
  return defaultSitemap;
};
